import io from 'socket.io-client';
import setCookie from 'set-cookie-parser';
import { useState, useEffect } from 'react';
import {
    Subscription,
    SubscriptionHandler,
    SubscriptionListener
} from "./subscriptions";

export enum ConnectionState {
    Disconnected = "disconnected",
    Connecting = "connecting",
    Connected = "connected"
}

export class Connection {
    private _state : ConnectionState = ConnectionState.Disconnected;
    private _socket : SocketIOClient.Socket | null = null;
    private _hostname : string | null = null;

    private subscriptions : SubscriptionHandler<ConnectionState> = new SubscriptionHandler<ConnectionState>();

    private static instance : Connection | null = null;

    static get() : Connection {
        if (!Connection.instance) {
            Connection.instance = new Connection();
        }
        return Connection.instance;
    }

    async initialize(apiUrl: string) {
        this._hostname = apiUrl;
        const token: string | null = await this.fetchToken(apiUrl);
        await this.connect(apiUrl, token);
    }

    get state() {
        return this._state;
    }

    get socket() {
        if (!this._socket) {
            throw new Error("Socket not available");
        }
        return this._socket;
    }

    get hostname() {
        if (!this._hostname) {
            throw new Error("Hostname not available");
        }
        return this._hostname;
    }

    send<T = unknown>(command : string, message : object): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            if (!this._socket || this._state !== ConnectionState.Connected) {
                throw new Error("Not connected");
            }

            this._socket.emit(command, message, (err : string | Error | null, response : T) => {
                if (err) {
                    return reject(err);
                }
                resolve(response);
            });
        });
    }

    addListener(listener : SubscriptionListener<ConnectionState>) : Subscription<ConnectionState> {
        return this.subscriptions.add(listener);
    }

    private setState(state: ConnectionState) {
        if (this._state === state) {
            return;
        }
        
        this._state = state;

        this.subscriptions.publish(this._state);
    }

    private async fetchToken(apiUrl: string) {
        const separator = apiUrl.endsWith('/') ? '' : '/'
        const tokenUrl = (apiUrl ? apiUrl : '') + separator + 'token';

        try {
            const response = await fetch(tokenUrl, {
                credentials: "include"
            });
            return this.getFromFetchCookie(response);
        } catch (e) {
            return new Promise<string>((resolve, reject) => {
                const separator = apiUrl.endsWith('/') ? '' : '/';
                const tokenUrl = (apiUrl ? apiUrl : '') + separator + 'token';
        
                const xhr = new XMLHttpRequest();
                xhr.open('GET', tokenUrl, true);
                xhr.withCredentials = true;
        
                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 400) {
                        const token = this.getFromCookie(xhr);
                        if (token !== null) {
                            resolve(token);
                        } else {
                            reject(new Error('Token not found in cookies'));
                        }
                    } else {
                        reject(new Error('Request failed with status ' + xhr.status));
                    }
                };
        
                xhr.onerror = () => {
                    const rootMessage = e instanceof Error ? e.message : 'Unknown error';
                    reject(new Error(`Network error while requesting ${tokenUrl} (${rootMessage})`));
                };
        
                xhr.send();
            });
        }
    }

    private getFromFetchCookie(response: Response) {
        const responseCookieHeader = response.headers.get('set-cookie');
        const combinedCookieHeader = responseCookieHeader ?? document.cookie;
        const splitCookieHeaders = setCookie.splitCookiesString(combinedCookieHeader);
        const cookies = setCookie.parse(splitCookieHeaders);
        if(cookies.length > 0) {
            return cookies[0].value;
        }
        return null;
    }

    private getFromCookie(response: XMLHttpRequest) {
        const combinedCookieHeader = response.getResponseHeader('set-cookie') || document.cookie;
        const splitCookieHeaders = setCookie.splitCookiesString(combinedCookieHeader);
        const cookies = setCookie.parse(splitCookieHeaders);
        if (cookies.length > 0) {
            return cookies[0].value;
        }
        return null;
    }

    private onUnauthorized() {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { Session } = require('./sessions');
        Session.get().onUnauthorized();
        if(this._hostname) {
            this.initialize(this._hostname);
        }
    }

    private async connect(apiUrl: string, token: string | null) {
        if (this._socket) {
            this._socket.removeAllListeners();
            this._socket.disconnect();
        }

        this._socket = io.connect(apiUrl, {
            query: "connect.sid=" + token
        });
        
        this._socket.on('error', (err : string) => {
            if (err === "Not authorized") {
                this.onUnauthorized();
            }
        });

        this._socket.on('connect', () => this.setState(ConnectionState.Connected));
        this._socket.on('reconnecting', () => this.setState(ConnectionState.Connecting));
        this._socket.on('disconnect', () => this.setState(ConnectionState.Disconnected));
        this._socket.on('connect_error', () => this.setState(ConnectionState.Disconnected));

        this.setState(ConnectionState.Connecting);
    }
}

export const useConnectionState = () => {
    const [state, setState] = useState<ConnectionState>(ConnectionState.Connecting);
    const [isConnected, setIsConnected] = useState(Connection.get().state === ConnectionState.Connected);

    useEffect(() => {
        const subscription = Connection.get().addListener((state) => {
            setState(state);
            setIsConnected(state === ConnectionState.Connected);
        });

        return () => subscription.remove();
    }, []);

    return {
        state,
        isConnected
    };
}
